// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { apiUrl } from "../config/api";
import { useMaintenance } from "./MaintenanceContext"; // ✅ A) Import Maintenance

const AuthContext = createContext();
const API_BASE = apiUrl("/api");

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ B) get setMaintenance
  const { setMaintenance } = useMaintenance();

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      setLoading(false);
      return;
    }

    fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setUser(data.user);
          localStorage.setItem("admin_user", JSON.stringify(data.user));

          // ✅ D) ping a protected endpoint to detect maintenance immediately
          // (users without Settings permission may get 403, that's fine)
          fetch(`${API_BASE}/settings`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => {});
        } else {
          logout();
        }
      })
      .catch(() => logout())
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return {
          success: false,
          error: data.error || data.message || "Invalid email or password",
        };
      }

      if (data.user.status && data.user.status !== "Active") {
        return { success: false, error: `Account is ${data.user.status}. Contact admin.` };
      }

      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_user", JSON.stringify(data.user));
      setUser(data.user);

      return { success: true, user: data.user };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: "Server error. Please try again." };
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    setUser(null);
  };

  // ✅ C) supports JSON AND FormData + detects maintenance 503
  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem("admin_token");

    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };

    const isFormData = options.body instanceof FormData;
    if (!isFormData && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    const res = await fetch(`${API_BASE}${url}`, { ...options, headers });

    // ✅ Maintenance handling
    if (res.status === 503) {
      const data = await res.clone().json().catch(() => ({}));
      if (data?.code === "MAINTENANCE_MODE") {
        setMaintenance({
          active: true,
          message: data.message || "",
          startsAt: data.startsAt || null,
          endsAt: data.endsAt || null,
        });
      }
    } else {
      // ✅ if maintenance was active before and now API is OK, turn it off
      setMaintenance((prev) => (prev?.active ? { ...prev, active: false } : prev));
    }

    return res;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};